/* The only two things this app keeps in the browser: the language choice and the 24-hour flag that stops the sign-up
   form from showing again after the age question said no. Never tokens, never personal data.
   Storage can be missing or throw (private windows, blocked site data), so every access is wrapped and there is an
   in-memory fallback that lasts until the page reloads. */
import { isLang, type Lang } from '@pathabroad/core/browser';

export const LANG_KEY = 'pa_lang';
export const AGE_BLOCK_KEY = 'pa_age_block';
export const AGE_BLOCK_MS = 24 * 60 * 60 * 1000;

const memory: { lang: Lang | null; blockUntil: number | null } = { lang: null, blockUntil: null };

function store(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

export function readLang(): Lang | null {
  try {
    const v = store()?.getItem(LANG_KEY);
    if (isLang(v)) return v;
  } catch { /* storage blocked */ }
  return memory.lang;
}

export function writeLang(lang: Lang): void {
  memory.lang = lang;
  try { store()?.setItem(LANG_KEY, lang); } catch { /* storage blocked */ }
}

export function setAgeBlock(now: number = Date.now()): void {
  const until = now + AGE_BLOCK_MS;
  memory.blockUntil = until;
  try { store()?.setItem(AGE_BLOCK_KEY, String(until)); } catch { /* storage blocked */ }
}

export function isAgeBlocked(now: number = Date.now()): boolean {
  let until = memory.blockUntil ?? 0;
  try {
    let v = Number(store()?.getItem(AGE_BLOCK_KEY) || 0);
    /* more than 24 hours ahead (the clock moved back, or not a value we wrote): the 24 hours restart from now */
    if (Number.isFinite(v) && v > now + AGE_BLOCK_MS) {
      v = now + AGE_BLOCK_MS;
      store()?.setItem(AGE_BLOCK_KEY, String(v));
    }
    if (Number.isFinite(v) && v > until) until = v;
  } catch { /* storage blocked */ }
  if (until > now) return true;
  if (until) {
    memory.blockUntil = null;
    try { store()?.removeItem(AGE_BLOCK_KEY); } catch { /* storage blocked */ }
  }
  return false;
}

/* test hook */
export function resetMemory(): void { memory.lang = null; memory.blockUntil = null; }
